/**
 * ForecastCard Component
 *
 * UI/Aesthetics: Card component displaying rebate forecast data with interactive line chart and optional status cards
 * Functions:
 * - Displays forecast data using Recharts line chart
 * - Shows actual vs predicted rebate amounts over time
 * - Custom tooltip with formatted currency values
 * - Optional status cards integration
 * - Responsive chart sizing and formatting
 * Previous: Used MUI Card + MDBox + MDTypography with sx styling
 * Now: Uses shadcn Card + native elements with Tailwind classes
 */

import React, { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipContentProps,
  ReferenceLine,
  DefaultLegendContentProps,
} from 'recharts';
import {
  MultiYearForecastData,
  getPredictionStrategyColors,
  LINE_TYPE_LABELS,
  LineType,
} from '@/types/rebateAnalytics';
import {
  FORECAST_LINE_VISIBILITY,
  PREVIOUS_YEAR_PREDICTION_GRAY,
  PREVIOUS_YEAR_STROKE_DASH,
} from 'src/components/rebates/ForecastCardConstants';
import { T, Var, useGT } from 'gt-react';
import RebateStatusCards from 'src/components/rebates/RebateStatusCards';

// Minimal palette (formerly from the removed MUI md-theme colors)
const colors = {
  success: { main: '#84cc16' },
  black: { main: '#000000' },
  grey: {
    400: '#a8a29e',
    500: '#78716c',
    600: '#57534e',
  } as Record<number, string>,
};
import {
  transformForecastData,
  convertToRechartsData,
  chartFormatters,
  chartConfig,
  RechartsDataPoint,
} from 'src/utils/chartUtils';
import { calculateConditionalVsNonConditionalRebates } from 'src/utils/rebateUtils';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  formatCurrency,
  calculateOverallRebateMetrics,
} from '@erp/shared-utils';

interface ForecastCardProps {
  multiYearForecastData?: MultiYearForecastData;
  title?: string;
  showStatusCards?: boolean;
  compact?: boolean;
  rebateRules?: Array<{
    id: string;
    name?: string;
    isConditional: boolean;
  }>;
  isLoadingForecast?: boolean; // Whether forecast data is loading
  isCumulative?: boolean; // Whether data is cumulative (affects status card labels)
  currentDate?: Date; // Optional current date for date-based calculations. Defaults to new Date() if not provided.
  predictionSplitDay?: number | null; // Optional split day for past/future predictions. If provided, will be used instead of calculating it.
  reportedSplitDay?: number | null; // Optional split day for reported data (based on today's date). Separates past reported (black) from future reported (blue).
  ruleTagsMap?: Map<string, string[]>; // Map of ruleId -> tag names
  visibleLines?: Set<string>; // Set of visible line types (defaults to all visible if not provided)
  onVisibleLinesChange?: (lines: Set<string>) => void; // Handler for visibility changes (optional)
}

export type { ForecastCardProps };

const ForecastCard: React.FC<ForecastCardProps> = ({
  multiYearForecastData,
  title,
  showStatusCards = false,
  compact = false,
  rebateRules,
  isLoadingForecast = false,
  isCumulative = false,
  currentDate,
  predictionSplitDay,
  reportedSplitDay,
  ruleTagsMap,
  visibleLines,
  onVisibleLinesChange,
}) => {
  const gt = useGT();

  // Use multiYearForecastData if provided, otherwise fall back to legacy forecast prop
  // This is the transformed data (cumulative if isCumulative=true, incremental if false)
  const currentYearData = multiYearForecastData?.currentYearData;
  const previousYearData = multiYearForecastData?.previousYearData ?? null;

  // Default to all lines visible if not provided
  const effectiveVisibleLines = useMemo(() => {
    if (!visibleLines) {
      const defaultSet = new Set<string>([
        FORECAST_LINE_VISIBILITY.estimates,
        FORECAST_LINE_VISIBILITY.reported,
        FORECAST_LINE_VISIBILITY.previousYear,
      ]);
      // Add all prediction strategy keys from data
      if (currentYearData?.predictions) {
        Object.keys(currentYearData.predictions).forEach((key) =>
          defaultSet.add(key),
        );
      }
      return defaultSet;
    }
    return visibleLines;
  }, [visibleLines, currentYearData?.predictions]);

  // Data comes from backend with both incremental and cumulative values
  // We just select which field to display based on isCumulative flag
  const processedCurrentYearData = useMemo(() => {
    if (!currentYearData) return null;
    return transformForecastData(
      currentYearData,
      isCumulative,
      predictionSplitDay,
      reportedSplitDay,
    );
  }, [currentYearData, isCumulative, predictionSplitDay, reportedSplitDay]);

  // Transform previous year data if available
  // Note: previous year doesn't use predictionSplitDay/lastRealEstimate (those are for current year)
  const processedPreviousYearData = useMemo(() => {
    if (!previousYearData) {
      return null;
    }
    const processed = transformForecastData(previousYearData, isCumulative);
    return processed;
  }, [previousYearData, isCumulative]);

  // Color for the future portion of reported line — matches the prediction strategy used to generate it
  const reportedFutureColor = useMemo(() => {
    const algorithm = currentYearData?.reportedAlgorithm ?? 'linear-regression';
    return getPredictionStrategyColors(algorithm).future;
  }, [currentYearData?.reportedAlgorithm]);

  // Convert current year data to Recharts format
  const currentYearChartData = useMemo(() => {
    if (!processedCurrentYearData) return [];
    // Data is already in the correct format (cumulative or incremental) from parent
    // Just convert to Recharts format
    return convertToRechartsData(processedCurrentYearData);
  }, [processedCurrentYearData]);

  // Convert previous year data to Recharts format and align by daysFromStart
  const previousYearChartData = useMemo(() => {
    if (!processedPreviousYearData) {
      return [];
    }
    const chartData = convertToRechartsData(processedPreviousYearData);
    return chartData;
  }, [processedPreviousYearData]);

  // Merge chart data: combine current and previous year data by daysFromStart
  // Previous year data will be added with a prefix to distinguish it
  // Always use current year's dateStr for x-axis to avoid duplicate labels
  const chartData = useMemo(() => {
    const dayMap = new Map<number, RechartsDataPoint>();

    // Add current year data first - this establishes the date strings for x-axis
    currentYearChartData.forEach((point) => {
      const day = point.day;
      dayMap.set(day, { ...point });
    });

    // Add previous year data with prefix, aligned by daysFromStart
    // Only merge into existing entries (from current year) to ensure consistent x-axis labels
    previousYearChartData.forEach((point) => {
      const day = point.day;
      const existing = dayMap.get(day);

      // Only add previous year data if there's a corresponding current year entry
      // This ensures x-axis labels come from current year only
      if (existing) {
        // Add previous year values with prefix
        Object.keys(point).forEach((key) => {
          if (key !== 'day' && key !== 'dateStr' && key !== 'fullDateStr') {
            existing[`prev_${key}`] = point[key];
          }
        });
        dayMap.set(day, existing);
      }
      // If no current year entry exists for this day, skip it to avoid duplicate x-axis labels
    });

    // Convert map to array and sort by day
    return Array.from(dayMap.values()).sort((a, b) => a.day - b.day);
  }, [currentYearChartData, previousYearChartData]);

  // Get current position from transformed data (already cumulative or incremental based on parent)
  // Select estimate based on current month/year:
  // - If current month/year exists in data, use that
  // - If data is from a past year, use the last estimate with breakdowns
  const currentActualPosition = useMemo(() => {
    if (!currentYearData?.estimates || currentYearData.estimates.length === 0) {
      return null;
    }

    const now = currentDate || new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-indexed (0 = January, 11 = December)

    // Sort estimates by date
    const sortedEstimates = [...currentYearData.estimates].sort(
      (a, b) => a.date.getTime() - b.date.getTime(),
    );

    // Filter to estimates with breakdowns (prefer those with data)
    const estimatesWithBreakdowns = sortedEstimates.filter(
      (est) => est.breakdowns && est.breakdowns.length > 0,
    );

    // Try to find an estimate that matches the current month/year
    const matchingEstimate = estimatesWithBreakdowns.find((estimate) => {
      const estimateYear = estimate.date.getFullYear();
      const estimateMonth = estimate.date.getMonth();
      return estimateYear === currentYear && estimateMonth === currentMonth;
    });

    // If we found a match for current month/year, use it
    if (matchingEstimate) {
      return matchingEstimate;
    }

    // Otherwise, use the last estimate with breakdowns (most recent data point)
    if (estimatesWithBreakdowns.length > 0) {
      return estimatesWithBreakdowns[estimatesWithBreakdowns.length - 1];
    }

    // Fallback to last estimate even if no breakdowns (shouldn't happen but be safe)
    return sortedEstimates[sortedEstimates.length - 1];
  }, [currentYearData, currentDate]);

  const currentPredictedPosition = useMemo(() => {
    const algorithm = currentYearData?.reportedAlgorithm ?? 'linear-regression';
    if (
      !currentYearData?.predictions[algorithm] ||
      currentYearData.predictions[algorithm].length === 0
    ) {
      return null;
    }

    const now = currentDate || new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-indexed (0 = January, 11 = December)

    // Sort estimates by date
    const sortedPredictions = [...currentYearData.predictions[algorithm]].sort(
      (a, b) => a.date.getTime() - b.date.getTime(),
    );

    // Try to find an estimate that matches the current month/year
    const matchingEstimate = sortedPredictions.find((prediction) => {
      const predictionYear = prediction.date.getFullYear();
      const predictionMonth = prediction.date.getMonth();
      return predictionYear === currentYear && predictionMonth === currentMonth;
    });

    // If we found a match for current month/year, use it
    if (matchingEstimate) {
      return matchingEstimate;
    }

    // Otherwise, use the last estimate (most recent data point)
    return sortedPredictions[sortedPredictions.length - 1];
  }, [currentYearData, currentDate]);

  // Get current reported position (similar to currentActualPosition but for reported data)
  const currentReportedPosition = useMemo(() => {
    if (!currentYearData?.reported || currentYearData.reported.length === 0) {
      return null;
    }

    const now = currentDate || new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-indexed (0 = January, 11 = December)

    // Sort reported by date
    const sortedReported = [...currentYearData.reported].sort(
      (a, b) => a.date.getTime() - b.date.getTime(),
    );

    // Filter to reported with breakdowns (prefer those with data)
    const reportedWithBreakdowns = sortedReported.filter(
      (rep) => rep.breakdowns && rep.breakdowns.length > 0,
    );

    // Try to find a reported point that matches the current month/year
    const matchingReported = reportedWithBreakdowns.find((reported) => {
      const reportedYear = reported.date.getFullYear();
      const reportedMonth = reported.date.getMonth();
      return reportedYear === currentYear && reportedMonth === currentMonth;
    });

    // If we found a match for current month/year, use it
    if (matchingReported) {
      return matchingReported;
    }

    // Otherwise, use the last reported with breakdowns (most recent data point)
    if (reportedWithBreakdowns.length > 0) {
      return reportedWithBreakdowns[reportedWithBreakdowns.length - 1];
    }

    // Fallback to last reported even if no breakdowns (shouldn't happen but be safe)
    return sortedReported[sortedReported.length - 1];
  }, [currentYearData, currentDate]);

  // Calculate Y-axis domain and ticks for better spacing
  // Only include values from visible lines to ensure proper scaling when lines are toggled
  const { yAxisDomain, yAxisTicks } = useMemo(() => {
    if (chartData.length === 0) {
      return {
        yAxisDomain: [0, (dataMax: number) => dataMax * 1.02] as [
          number,
          (dataMax: number) => number,
        ],
        yAxisTicks: [] as number[],
      };
    }

    // Determine which data keys correspond to visible lines
    const visibleDataKeys = new Set<string>();

    // Map visible line types to their data keys in chartData
    if (effectiveVisibleLines.has(FORECAST_LINE_VISIBILITY.estimates)) {
      visibleDataKeys.add('estimates');
    }

    // Add prediction types for each visible strategy
    if (processedCurrentYearData?.types) {
      processedCurrentYearData.types
        .filter(
          (type) =>
            type !== 'estimates' &&
            type !== 'reported' &&
            type !== 'reported_future',
        )
        .forEach((type) => {
          // Get the strategy key (strip '-past' suffix)
          const strategyKey = type.endsWith('-past')
            ? type.replace(/-past$/, '')
            : type;
          if (effectiveVisibleLines.has(strategyKey)) {
            visibleDataKeys.add(type);
          }
        });
    }

    if (effectiveVisibleLines.has(FORECAST_LINE_VISIBILITY.reported)) {
      visibleDataKeys.add('reported');
      visibleDataKeys.add('reported_future'); // Extended future reported points
    }

    if (effectiveVisibleLines.has(FORECAST_LINE_VISIBILITY.previousYear)) {
      // Add previous year data keys (prev_estimates, prev_linear-regression, etc.)
      if (processedPreviousYearData?.types) {
        processedPreviousYearData.types.forEach((type) => {
          visibleDataKeys.add(`prev_${type}`);
        });
      }
    }

    // Extract only numeric values from visible data keys (excluding metadata fields)
    const allValues = chartData.flatMap((dataPoint) =>
      Object.entries(dataPoint)
        .filter(
          ([key]) =>
            key !== 'day' &&
            key !== 'dateStr' &&
            key !== 'fullDateStr' &&
            visibleDataKeys.has(key),
        )
        .map(([, value]) => (typeof value === 'number' ? value : 0)),
    );

    // Safety check: if no values found, default to [0, 0]
    if (allValues.length === 0) {
      return {
        yAxisDomain: [0, 100] as [number, number],
        yAxisTicks: [0, 25, 50, 75, 100] as number[],
      };
    }

    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);

    // If there are negative values, allow domain to go below 0
    // Otherwise, start at 0 for better visualization
    const domainMin = minValue < 0 ? minValue * 1.02 : 0;
    const domainMax = maxValue * 1.02;

    const yAxisDomain: [number, number] = [domainMin, domainMax];

    let yAxisTicks = chartConfig.getYAxisTicks(minValue, maxValue);

    // Ensure $0 is always included in the ticks
    if (!yAxisTicks.includes(0)) {
      yAxisTicks = [...yAxisTicks, 0].sort((a, b) => a - b);
    }

    return { yAxisDomain, yAxisTicks };
  }, [
    chartData,
    effectiveVisibleLines,
    processedCurrentYearData?.types,
    processedPreviousYearData?.types,
  ]);
  // Calculate totals from transformed data (already cumulative or incremental based on parent)
  const totals = useMemo(() => {
    if (!processedCurrentYearData) {
      return { totalOwed: 0, totalActual: 0 };
    }

    // Use totals from processed data (already in correct format from parent)
    const totalActual = processedCurrentYearData.totals.actualToDate;
    const totalOwed = processedCurrentYearData.totals.projectedTotal;

    return { totalOwed, totalActual };
  }, [processedCurrentYearData]);

  // Check if current year has any data (estimates, predictions, or reported)
  const hasCurrentYearData = useMemo(() => {
    if (!currentYearData) return false;

    const hasEstimates =
      currentYearData.estimates && currentYearData.estimates.length > 0;
    const hasPredictions =
      currentYearData.predictions &&
      Object.values(currentYearData.predictions).some(
        (points) => points && points.length > 0,
      );
    const hasReported =
      currentYearData.reported && currentYearData.reported.length > 0;

    return hasEstimates || hasPredictions || hasReported;
  }, [currentYearData]);

  // Calculate conditional vs non-conditional rebate breakdown
  const rebateBreakdown = useMemo(() => {
    if (!currentActualPosition) {
      return {
        conditionalRebateYtd: undefined,
        nonConditionalRebateYtd: undefined,
      };
    }
    return calculateConditionalVsNonConditionalRebates(
      currentActualPosition,
      rebateRules || [],
      isCumulative,
    );
  }, [currentActualPosition, rebateRules, isCumulative]);

  // Calculate tag totals from forecast breakdowns
  const tagTotals = useMemo(() => {
    if (!ruleTagsMap || !currentActualPosition) {
      return new Map<string, number>();
    }

    const totals = new Map<string, number>();

    const breakdownsToProcess = currentActualPosition.breakdowns || [];

    // Iterate through breakdowns and aggregate by tag
    breakdownsToProcess.forEach((breakdown) => {
      const ruleTags = ruleTagsMap.get(breakdown.ruleId);
      if (!ruleTags || ruleTags.length === 0) {
        // Skip rules with no tags (user confirmed: exclude from tag-based cards)
        return;
      }

      // Select the appropriate amount based on isCumulative
      const amount = isCumulative
        ? (breakdown.cumulativeRebateAmount ?? breakdown.rebateAmount)
        : breakdown.rebateAmount;

      // Add this breakdown's amount to all tags associated with the rule
      ruleTags.forEach((tagName) => {
        const currentTotal = totals.get(tagName) || 0;
        totals.set(tagName, currentTotal + amount);
      });
    });

    // Filter out tags with zero totals
    const nonZeroTotals = new Map<string, number>();
    totals.forEach((amount, tagName) => {
      if (Math.abs(amount) >= 0.01) {
        nonZeroTotals.set(tagName, amount);
      }
    });

    return nonZeroTotals;
  }, [ruleTagsMap, currentActualPosition, isCumulative]);

  // Custom tooltip component
  const CustomTooltip = ({
    active,
    payload,
    label,
  }: Partial<TooltipContentProps<number, string>>) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload as RechartsDataPoint;
      // Filter out null/undefined values
      const validPayload = payload.filter(
        (entry) => entry.value !== null && entry.value !== undefined,
      );

      // Check if we're at a connection point (where past and future lines meet)
      // At connection points, we have both past and future versions
      // Find any prediction strategy connection points dynamically
      const connectionFutureKeys = new Set<string>();
      Object.keys(data).forEach((key) => {
        if (key.endsWith('-past')) {
          const futureKey = key.replace(/-past$/, '');
          if (
            data[key] !== null &&
            data[key] !== undefined &&
            data[futureKey] !== null &&
            data[futureKey] !== undefined
          ) {
            connectionFutureKeys.add(futureKey);
          }
        }
      });
      const hasReportedPast =
        data['reported'] !== null && data['reported'] !== undefined;
      const hasReportedFuture =
        data['reported_future'] !== null &&
        data['reported_future'] !== undefined;

      if (hasReportedPast && hasReportedFuture) {
        connectionFutureKeys.add('reported_future');
      }

      // At connection points, filter out future lines (duplicates of past)
      const filteredPayload =
        connectionFutureKeys.size > 0
          ? validPayload.filter(
              (entry) => !connectionFutureKeys.has(entry.dataKey as string),
            )
          : validPayload;

      return (
        <div className="bg-white/95 text-gray-800 p-3 rounded-lg border border-gray-200 shadow-lg">
          <p className="mb-2 font-bold text-gray-800">
            <T context="rebate forecast chart tooltip: day index and calendar date">
              Day <Var>{Number(label) + 1}</Var> • <Var>{data.fullDateStr}</Var>
            </T>
          </p>

          {filteredPayload.map((entry, index: number) => (
            <p key={index} className="my-1 text-gray-800 font-medium">
              <span
                className="inline-block w-3 h-3 rounded-full mr-2"
                style={{ backgroundColor: entry.color }}
              ></span>
              <Var>{entry.name}</Var>:{' '}
              <Var>{formatCurrency(entry.value as number)}</Var>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Custom Legend with chevrons for split lines and clickable toggle buttons
  const CustomLegend = ({ payload }: DefaultLegendContentProps) => {
    // Build legend entries based on what SHOULD be shown, not just what's in payload
    // This ensures hidden lines still appear in the legend so they can be re-enabled
    const legendEntries: Array<{
      label: string;
      colors: string[];
      isSplit: boolean;
      lineType: string; // LineType for visibility toggle ('estimates', 'predictions', 'reported', 'previousYear')
    }> = [];

    // Helper to get color from payload if available, otherwise use default
    const getColorFromPayload = (
      dataKey: string,
      defaultColor: string,
    ): string => {
      if (!payload) return defaultColor;
      const entry = payload.find((e) => e.dataKey === dataKey);
      return (entry?.color as string) || defaultColor;
    };

    const hasReportedPast = payload?.some(
      (entry) => entry.dataKey === 'reported',
    );
    const hasReportedFuture = payload?.some(
      (entry) => entry.dataKey === 'reported_future',
    );

    // Check if we have data for each line type
    const hasEstimates =
      processedCurrentYearData?.types?.includes('estimates') ?? false;
    const hasReported =
      processedCurrentYearData?.types?.includes('reported') ?? false;
    const hasPreviousYear = processedPreviousYearData !== null;

    // Discover prediction strategies from processed types
    const predictionStrategies = new Set<string>();
    processedCurrentYearData?.types?.forEach((type) => {
      if (
        type !== 'estimates' &&
        type !== 'reported' &&
        type !== 'reported_future'
      ) {
        // Strip '-past' suffix to get the strategy key
        const strategyKey = type.endsWith('-past')
          ? type.replace(/-past$/, '')
          : type;
        predictionStrategies.add(strategyKey);
      }
    });

    // Always show Estimates if data exists
    if (hasEstimates) {
      legendEntries.push({
        label: LINE_TYPE_LABELS.estimates,
        colors: [getColorFromPayload('estimates', colors.success.main)],
        isSplit: false,
        lineType: FORECAST_LINE_VISIBILITY.estimates,
      });
    }

    // Show each prediction strategy as its own legend entry
    predictionStrategies.forEach((strategy) => {
      const strategyColors = getPredictionStrategyColors(strategy);
      const pastType = `${strategy}-past`;
      const hasPast = payload?.some((entry) => entry.dataKey === pastType);
      const hasFuture = payload?.some((entry) => entry.dataKey === strategy);
      const hasSplit = hasPast && hasFuture;

      if (hasSplit) {
        legendEntries.push({
          label: LINE_TYPE_LABELS[strategy as LineType] || strategy,
          colors: [
            getColorFromPayload(pastType, strategyColors.past),
            getColorFromPayload(strategy, strategyColors.future),
          ],
          isSplit: true,
          lineType: strategy,
        });
      } else {
        legendEntries.push({
          label: LINE_TYPE_LABELS[strategy as LineType] || strategy,
          colors: [getColorFromPayload(strategy, strategyColors.future)],
          isSplit: false,
          lineType: strategy,
        });
      }
    });

    // Always show Reported if data exists
    if (hasReported) {
      const hasSplit = hasReportedPast && hasReportedFuture;
      if (hasSplit) {
        legendEntries.push({
          label: LINE_TYPE_LABELS.reported,
          colors: [
            getColorFromPayload('reported', colors.black.main), // Black
            getColorFromPayload('reported_future', reportedFutureColor),
          ],
          isSplit: true,
          lineType: FORECAST_LINE_VISIBILITY.reported,
        });
      } else {
        legendEntries.push({
          label: LINE_TYPE_LABELS.reported,
          colors: [getColorFromPayload('reported', colors.black.main)],
          isSplit: false,
          lineType: FORECAST_LINE_VISIBILITY.reported,
        });
      }
    }

    // Always show Previous Year if data exists
    if (hasPreviousYear) {
      legendEntries.push({
        label: LINE_TYPE_LABELS.previousYear,
        colors: [getColorFromPayload('prev_estimates', colors.grey[600])],
        isSplit: false,
        lineType: FORECAST_LINE_VISIBILITY.previousYear,
      });
    }

    // Handle visibility toggle
    const handleToggleVisibility = (lineType: string) => {
      if (!onVisibleLinesChange) return;

      const isVisible = effectiveVisibleLines.has(lineType);
      const newVisible = new Set(effectiveVisibleLines);

      // Prevent unchecking if it's the last visible line
      if (isVisible && effectiveVisibleLines.size === 1) {
        return; // Don't allow unchecking last line
      }

      if (isVisible) {
        newVisible.delete(lineType);
      } else {
        newVisible.add(lineType);
      }
      onVisibleLinesChange(newVisible);
    };

    // Don't render if no entries (shouldn't happen, but safety check)
    if (legendEntries.length === 0) {
      return null;
    }

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: '40px',
          paddingTop: '20px',
        }}
      >
        {legendEntries.map((entry, index) => {
          const isVisible = effectiveVisibleLines.has(entry.lineType);
          const isDisabled =
            !onVisibleLinesChange ||
            (isVisible && effectiveVisibleLines.size === 1);

          return (
            <button
              key={index}
              type="button"
              onClick={() => handleToggleVisibility(entry.lineType)}
              disabled={isDisabled}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 12px',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                backgroundColor: isVisible ? '#FFFFFF' : '#F9FAFB',
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                opacity: isVisible ? 1 : 0.5,
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                if (!isDisabled) {
                  e.currentTarget.style.backgroundColor = isVisible
                    ? '#F3F4F6'
                    : '#F9FAFB';
                  e.currentTarget.style.borderColor = '#D1D5DB';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = isVisible
                  ? '#FFFFFF'
                  : '#F9FAFB';
                e.currentTarget.style.borderColor = '#E5E7EB';
              }}
            >
              {entry.isSplit ? (
                // Split circle for split lines (left half one color, right half another)
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{
                    background: isVisible
                      ? `linear-gradient(to right, ${entry.colors[0]} 50%, ${entry.colors[1]} 50%)`
                      : 'linear-gradient(to right, #9CA3AF 50%, #9CA3AF 50%)',
                  }}
                />
              ) : (
                // Solid circle for single-segment lines
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{
                    backgroundColor: isVisible ? entry.colors[0] : '#9CA3AF',
                  }}
                />
              )}
              <span
                style={{
                  color: isVisible ? '#374151' : '#9CA3AF',
                  fontSize: '14px',
                }}
              >
                {entry.label}
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  // Use shared formatting utilities
  const formatYAxis = chartFormatters.formatYAxis;

  // Generate month-end ticks based on forecast period (using UTC to avoid timezone issues)
  const xAxisTicks = useMemo(() => {
    const period = currentYearData?.forecastPeriod;
    if (!period?.startDate) {
      // Default: approximate month-end days for a calendar year starting Jan 1
      // Include day 0 for Jan 1
      return [0, 30, 58, 89, 119, 150, 180, 211, 242, 272, 303, 333, 364];
    }

    const startDate = new Date(period.startDate);
    const endDate = new Date(period.endDate);
    const ticks: number[] = [0]; // Start with day 0 (period start date)

    // Use UTC to avoid timezone issues - backend sends UTC dates
    let currentYear = startDate.getUTCFullYear();
    let currentMonth = startDate.getUTCMonth();
    const endYear = endDate.getUTCFullYear();
    const endMonth = endDate.getUTCMonth();

    while (
      currentYear < endYear ||
      (currentYear === endYear && currentMonth <= endMonth)
    ) {
      // Get last day of current month in UTC (day 0 of next month = last day of current month)
      const lastDayOfMonth = new Date(
        Date.UTC(currentYear, currentMonth + 1, 0),
      );

      // Calculate days from start using UTC timestamps
      const daysFromStart = Math.round(
        (lastDayOfMonth.getTime() - startDate.getTime()) /
          (1000 * 60 * 60 * 24),
      );

      if (daysFromStart >= 0 && lastDayOfMonth <= endDate) {
        ticks.push(daysFromStart);
      }

      // Move to next month
      currentMonth++;
      if (currentMonth > 11) {
        currentMonth = 0;
        currentYear++;
      }
    }

    return ticks;
  }, [currentYearData?.forecastPeriod]);

  // Format X-axis: calculate month label from day number (using UTC)
  const formatXAxis = (value: number) => {
    // First try to find in chart data
    const dataPoint = chartData.find((d) => d.day === value);
    if (dataPoint?.dateStr) {
      return dataPoint.dateStr;
    }

    // Calculate the date from day number and forecast period start (using UTC)
    const period = currentYearData?.forecastPeriod;
    if (period?.startDate) {
      const startDate = new Date(period.startDate);
      // Add days in milliseconds to avoid timezone issues
      const targetDate = new Date(
        startDate.getTime() + value * 24 * 60 * 60 * 1000,
      );

      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      return `${monthNames[targetDate.getUTCMonth()]} ${targetDate.getUTCDate()}`;
    }

    return value.toString();
  };

  // Common props for reference lines
  const referenceLineProps = {
    stroke: '#666',
    strokeWidth: 1,
    ifOverflow: 'extendDomain' as const,
  };

  return (
    <Card className="mb-6">
      <CardContent className="p-6">
        {/* Title */}
        {title && (
          <div className="mb-6">
            <h2 className="text-xl font-medium text-foreground mb-4">
              {title}
            </h2>
          </div>
        )}

        {/* Main Forecast Chart */}
        <div className={showStatusCards ? 'mb-6' : ''}>
          {isLoadingForecast ? (
            <div className="w-full" style={{ height: chartConfig.chartHeight }}>
              <LoadingSpinner
                text={gt('Loading forecast data...', {
                  context: 'loading state for rebate forecast chart',
                })}
                centered
                minHeight={chartConfig.chartHeight}
              />
            </div>
          ) : !currentYearData || !hasCurrentYearData ? (
            <div
              className="w-full flex flex-col items-center justify-center text-muted-foreground"
              style={{ height: chartConfig.chartHeight }}
            >
              <svg
                className="w-16 h-16 mb-4 text-muted-foreground/50"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              <p className="text-lg font-medium">
                <T context="empty state when rebate forecast chart has no data">
                  No forecast data available
                </T>
              </p>
              <p className="text-sm mt-1">
                <T context="empty state detail for rebate forecast chart">
                  There are no estimates, predictions, or reported values for
                  this period.
                </T>
              </p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={chartConfig.chartHeight}>
              <LineChart data={chartData} margin={chartConfig.chartMargin}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="day"
                  type="number"
                  tickFormatter={formatXAxis}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  ticks={xAxisTicks}
                  domain={[0, 365]}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={formatYAxis}
                  domain={yAxisDomain}
                  ticks={yAxisTicks}
                  axisLine={false}
                />
                <ReferenceLine y={0} {...referenceLineProps} />
                <ReferenceLine x={0} {...referenceLineProps} />
                <Tooltip content={<CustomTooltip />} />
                <Legend content={<CustomLegend />} />

                {/* Previous Year Actual Values Line (if available and visible) */}
                {effectiveVisibleLines.has(
                  FORECAST_LINE_VISIBILITY.previousYear,
                ) &&
                  processedPreviousYearData?.types.includes('estimates') && (
                    <Line
                      type="monotone"
                      dataKey="prev_estimates"
                      stroke={colors.grey?.[400] || '#9CA3AF'} // Gray/muted color for previous year
                      strokeWidth={3}
                      dot={{
                        fill: colors.grey?.[400] || '#9CA3AF',
                        strokeWidth: 2,
                        r: 4,
                      }}
                      name={gt('Previous Year ({year})', {
                        context:
                          'rebate forecast chart legend: prior fiscal year series',
                        year:
                          multiYearForecastData?.currentYear !== undefined
                            ? String(multiYearForecastData.currentYear - 1)
                            : gt('Previous', {
                                context:
                                  'fallback year label when prior year unknown',
                              }),
                      })}
                      activeDot={{
                        r: 6,
                        stroke: colors.grey?.[400] || '#9CA3AF',
                        strokeWidth: 2,
                      }}
                      connectNulls={false}
                      strokeDasharray="5 5" // Dashed line for previous year
                    />
                  )}
                {/* Previous Year: If no estimates, show predictions as main line */}
                {effectiveVisibleLines.has(
                  FORECAST_LINE_VISIBILITY.previousYear,
                ) &&
                  !processedPreviousYearData?.types.includes('estimates') &&
                  processedPreviousYearData?.types.some((type) =>
                    type.includes('linear-regression'),
                  ) && (
                    <Line
                      type="monotone"
                      dataKey="prev_linear-regression"
                      stroke={colors.grey?.[400] || '#9CA3AF'} // Gray/muted color for previous year
                      strokeWidth={3}
                      dot={{
                        fill: colors.grey?.[400] || '#9CA3AF',
                        strokeWidth: 2,
                        r: 4,
                      }}
                      name={gt('Previous Year ({year})', {
                        context:
                          'rebate forecast chart legend: prior fiscal year series',
                        year:
                          multiYearForecastData?.currentYear !== undefined
                            ? String(multiYearForecastData.currentYear - 1)
                            : gt('Previous', {
                                context:
                                  'fallback year label when prior year unknown',
                              }),
                      })}
                      activeDot={{
                        r: 6,
                        stroke: colors.grey?.[400] || '#9CA3AF',
                        strokeWidth: 2,
                      }}
                      connectNulls={false}
                      strokeDasharray="5 5" // Dashed line for previous year
                    />
                  )}

                {/* Previous Year Prediction Lines (if available and visible) */}
                {effectiveVisibleLines.has(
                  FORECAST_LINE_VISIBILITY.previousYear,
                ) &&
                  processedPreviousYearData?.types
                    .filter((type) => type !== 'estimates')
                    .map((seriesKey) => {
                      const prevDataKey = `prev_${seriesKey}`;
                      const lineColor =
                        colors.grey?.[500] || PREVIOUS_YEAR_PREDICTION_GRAY; // Lighter gray for previous year predictions
                      const dash = PREVIOUS_YEAR_STROKE_DASH;
                      const legendLabel = gt('Previous Year ({series})', {
                        context:
                          'rebate forecast chart legend: prior year prediction strategy',
                        series: seriesKey,
                      });

                      return (
                        <Line
                          key={prevDataKey}
                          type="monotone"
                          dataKey={prevDataKey}
                          stroke={lineColor}
                          strokeWidth={2}
                          strokeDasharray={dash}
                          dot={false}
                          name={legendLabel}
                          activeDot={{
                            r: 4,
                            stroke: lineColor,
                            strokeWidth: 1,
                          }}
                          connectNulls={false}
                        />
                      );
                    })}

                {/* Dynamic prediction strategy lines (future + past) */}
                {processedCurrentYearData?.types
                  .filter(
                    (type) =>
                      type !== 'estimates' &&
                      type !== 'reported' &&
                      type !== 'reported_future',
                  )
                  .map((type) => {
                    // Determine strategy key (strip '-past' suffix)
                    const isPast = type.endsWith('-past');
                    const strategyKey = isPast
                      ? type.replace(/-past$/, '')
                      : type;

                    // Only render if this strategy is visible
                    if (!effectiveVisibleLines.has(strategyKey)) return null;

                    const strategyColors =
                      getPredictionStrategyColors(strategyKey);
                    const strokeColor = isPast
                      ? strategyColors.past
                      : strategyColors.future;

                    return (
                      <Line
                        key={type}
                        type="monotone"
                        dataKey={type}
                        stroke={strokeColor}
                        strokeWidth={4}
                        dot={
                          isPast
                            ? { fill: strokeColor, strokeWidth: 2, r: 6 }
                            : false
                        }
                        name={
                          isPast
                            ? gt('{strategy} (Past)', {
                                context:
                                  'rebate forecast chart: prediction line segment before split day',
                                strategy:
                                  LINE_TYPE_LABELS[strategyKey as LineType] ||
                                  strategyKey,
                              })
                            : LINE_TYPE_LABELS[strategyKey as LineType] ||
                              strategyKey
                        }
                        activeDot={{
                          r: isPast ? 8 : 6,
                          stroke: strokeColor,
                          strokeWidth: 2,
                        }}
                        connectNulls={false}
                      />
                    );
                  })}

                {/* Reported Future (Blue) Line - extended future predictions */}
                {effectiveVisibleLines.has(FORECAST_LINE_VISIBILITY.reported) &&
                  processedCurrentYearData?.types.includes(
                    'reported_future',
                  ) && (
                    <Line
                      type="monotone"
                      dataKey="reported_future"
                      stroke={reportedFutureColor}
                      strokeWidth={4}
                      dot={{ fill: reportedFutureColor, strokeWidth: 2, r: 6 }}
                      name={gt('Reported (Future)', {
                        context:
                          'rebate forecast chart legend: extended reported projection',
                      })}
                      activeDot={{
                        r: 8,
                        stroke: reportedFutureColor,
                        strokeWidth: 2,
                      }}
                      connectNulls={false}
                    />
                  )}

                {/* Reported (Black) Line - real reported values (reported past) */}
                {effectiveVisibleLines.has(FORECAST_LINE_VISIBILITY.reported) &&
                  processedCurrentYearData?.types.includes('reported') && (
                    <Line
                      type="monotone"
                      dataKey="reported"
                      stroke={colors.black.main}
                      strokeWidth={4}
                      dot={{ fill: colors.black.main, strokeWidth: 2, r: 6 }}
                      name={gt('Reported (Black)', {
                        context:
                          'rebate forecast chart legend: actual reported amounts line color black',
                      })}
                      activeDot={{
                        r: 8,
                        stroke: colors.black.main,
                        strokeWidth: 2,
                      }}
                      connectNulls={false}
                    />
                  )}

                {/* Current Year Actual Values Line - Estimates (render last, top layer) */}
                {effectiveVisibleLines.has(
                  FORECAST_LINE_VISIBILITY.estimates,
                ) &&
                  processedCurrentYearData?.types.includes('estimates') && (
                    <Line
                      type="monotone"
                      dataKey="estimates"
                      stroke={colors.success.main}
                      strokeWidth={4}
                      dot={{ fill: colors.success.main, strokeWidth: 2, r: 6 }}
                      name={LINE_TYPE_LABELS.estimates}
                      activeDot={{
                        r: 8,
                        stroke: colors.success.main,
                        strokeWidth: 2,
                      }}
                      connectNulls={false}
                    />
                  )}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Status Cards - only show when we have data */}
        {showStatusCards && hasCurrentYearData && (
          <RebateStatusCards
            totalOwed={totals.totalActual}
            totalProjected={totals.totalOwed}
            currentActualPosition={currentActualPosition || undefined}
            currentPredictedPosition={currentPredictedPosition || undefined}
            currentReportedPosition={currentReportedPosition || undefined}
            compact={compact}
            summaryStyle={true}
            conditionalRebateYtd={rebateBreakdown.conditionalRebateYtd}
            nonConditionalRebateYtd={rebateBreakdown.nonConditionalRebateYtd}
            isCumulative={isCumulative}
            tagTotals={tagTotals}
            predictionAlgorithm={
              currentYearData?.reportedAlgorithm ?? 'linear-regression'
            }
            overallMetrics={
              currentYearData?.overallMetrics
                ? currentYearData.overallMetrics
                : currentYearData
                  ? {
                      // Fallback: calculate from breakdown records if backend doesn't provide overallMetrics
                      // Note: This will have n-counted volumes, so backend should always provide overallMetrics
                      actual: calculateOverallRebateMetrics(
                        currentYearData.estimates || [],
                        true, // Always cumulative for YTD percentage
                      ),
                      predicted: calculateOverallRebateMetrics(
                        currentYearData.predictions?.[
                          currentYearData.reportedAlgorithm ??
                            'linear-regression'
                        ] || [],
                        true, // Always cumulative for term-end percentage
                      ),
                    }
                  : undefined
            }
          />
        )}
      </CardContent>
    </Card>
  );
};

export default ForecastCard;
